import type { TFunction } from 'i18next';

/** Above this many seconds the wait is said in minutes. */
const SECONDS_IN_A_MINUTE = 60;

/**
 * Says a wait in the largest unit that does not round it away: seconds below a minute, minutes
 * rounded up from there.
 *
 * Never hours. The two caps are an hour and a day, and "in 1 hour" asks for the same thing as
 * "in 38 minutes" while sounding like a different order of magnitude.
 *
 * A missing or non numeric wait returns `undefined`, which is what makes the caller fall back to the
 * message that names no wait instead of writing one out of nothing.
 */
export const formatRetryAfter = (seconds: number | undefined, t: TFunction): string | undefined => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return undefined;

  if (seconds < SECONDS_IN_A_MINUTE) return t('retryAfter.seconds', { count: Math.ceil(seconds) });

  return t('retryAfter.minutes', { count: Math.ceil(seconds / SECONDS_IN_A_MINUTE) });
};

/**
 * Translates an inline error message, filling in the wait for the keys that name one.
 *
 * The value is passed to every key: the ones that do not name a wait ignore it, which is what keeps
 * the six places that show these messages from each deciding whether this one does.
 */
export const translateIdentityError = (
  t: TFunction,
  errorKey: string | undefined,
  retryAfterSeconds?: number,
): string | undefined => (errorKey ? t(errorKey, { wait: formatRetryAfter(retryAfterSeconds, t) }) : undefined);
